import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMicVAD } from '@ricky0123/vad-react';
import './App.css';

import Lottie from 'lottie-react';
import listeningAnimation from './listening-animation.json';

import {
  AppContainer,MainContent,

  Container,
  StatusBadge,
  StatusDot,
  ButtonWrapper,
  VoiceButton,
  LottieWrapper,
  StatusText,
  ConnectionIcon,
} from "./StyledComponents";

const randomId = (length = 10) => {
  const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return Array.from({ length }, () => characters.charAt(Math.floor(Math.random() * characters.length))).join('');
};
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============ UI MODE ============
// Set to true for the minimal advertisement-team UI:
// shows only credentials (API key, Tenant ID, Client ID), the connect button,
// and the chat display (user transcript + LLM response). Everything else is hidden.
// Set to false to show the full developer UI with all settings/parameters.
const MINIMAL_UI = true;

// ============ SETTINGS CONSTANTS ============
const TTS_PROVIDERS = [
  { value: 'tts_provider_1', label: 'TTS Provider 1' },
  { value: 'tts_provider_2', label: 'TTS Provider 2' },
];

// Each TTS provider has its own list of voices
const makeVoices = (count) =>
  Array.from({ length: count }, (_, i) => ({
    value: `voice${i + 1}`,
    label: `Voice ${i + 1}`,
  }));

const TTS_VOICES_BY_PROVIDER = {
  tts_provider_1: makeVoices(30),
  tts_provider_2: makeVoices(12),
};

const TTS_PACE_OPTIONS = [0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.2];

const STT_PROVIDERS = [
  { value: 'stt_provider_1', label: 'STT Provider 1' },
  { value: 'stt_provider_2', label: 'STT Provider 2' }
];

const SUPPORTED_LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  // { value: 'ko-KR', label: 'Korean (KR)' },
];

// Each STT provider supports its own set of locales
const LOCALES_BY_STT_PROVIDER = {
  stt_provider_1: [
    { value: 'en-US', label: 'English (US)' },
    // { value: 'ko-KR', label: 'Korean (KR)' },
  ],
  stt_provider_2: [
    { value: 'en-US', label: 'English (US)' },
    // { value: 'ko-KR', label: 'Korean (KR)' },
  ],
};

const RESPONSE_MODES = [
  { value: 'short', label: 'Short (Brief)' },
  { value: 'long', label: 'Long (Descriptive)' },
];

// const SAFETY_LEVELS = [
//   { value: 'very high', label: 'Very High' },
// ];


function App() {
  const [clientId] = useState(randomId());
  const [connected, setConnected] = useState(false);
  const [disconnectPopup, setDisconnectPopup] = useState({ show: false, reason: '' });
  const [notifications, setNotifications] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [vadStatus, setVadStatus] = useState('idle'); // idle, speaking, timeout
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [ice_status, setIceStatus] = useState(false);
  const [firstname, setFirstname] = useState("buddy");

  const [tenantApiKey, setTenantApiKey] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [inputClientId, setInputClientId] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Audio interruption states
  const [audioInterrupted, setAudioInterrupted] = useState(false);
  const [pausedAudioTime, setPausedAudioTime] = useState(0);
  const [audioBufferAvailable, setAudioBufferAvailable] = useState(false);
  
  // Configuration states
  const [maxSessionCount, setMaxSession] = useState(15);
  const [session_message, setSessionMessage] = useState('');
  const [personaInput, setPersonaInput] = useState('You are Miko, a friendly and helpful AI assistant.');
  const personaInputRef = useRef('You are Miko, a friendly and helpful AI assistant.');
  // Add near the other state declarations
  const [sessionActive, setSessionActive] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [sessionMode, setSessionMode] = useState('normal');
  const sessionModeRef = useRef('normal');

  // ============ NEW SETTINGS STATES ============

  const [sttProvider, setSttProvider] = useState('stt_provider_2');
  const [supportedLocales, setSupportedLocales] = useState(['en-US']);
  const [responseMode, setResponseMode] = useState('short');
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const cityInputRef = useRef(null);

  const [cityLoading, setCityLoading] = useState(false);
  const cityDebounceRef = useRef(null);
  const cityAbortRef = useRef(null);

  //Message logs
  const [messageLogs, setMessageLogs] = useState([]);

  // Custom datachannel message
  const [customMsgType, setCustomMsgType] = useState('');
  const [customMsgValue, setCustomMsgValue] = useState('');

  // Voice list from server
  const [voiceList, setVoiceList] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');

  // Age and turn count
  const [age, setAge] = useState('');
  const [turnCount, setTurnCount] = useState('');

  // Name and memory window size
  const [settingName, setSettingName] = useState('');
  const [memoryWindowSize, setMemoryWindowSize] = useState('');

  // Skill
  const [skill, setSkill] = useState('kiddopia');

  // Chat states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [selectedLocale, setSelectedLocale] = useState('en-US');
  const chatEndRef = useRef(null);

  const LOCALE_OPTIONS = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'ko-KR', label: 'Korean (KR)' }
  ];

  const audioRef = useRef(null);
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const timeoutRef = useRef(null);
  const streamingRef = useRef(false);
  const ws = useRef(null);
  
  // Audio context for more precise control
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const micTrackRef = useRef(null); // mic audio track, attached once at connect
  const micStreamRef = useRef(null); // underlying MediaStream, kept for cleanup
  const sendTransceiverRef = useRef(null); // the sendonly transceiver the mic is bound to
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioClipIndexRef = useRef(0);
  const audioDetectionIntervalRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const isRecordingActiveRef = useRef(false);
  const lastDcMessageRef = useRef(null);
  const inactivityIntervalRef = useRef(null);

  const SPEECH_TIMEOUT = 1500; // 1500ms timeout

  const log = (msg) => {
    console.log(msg);
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);
  };

  const sendDataChannelMessage = (payload) => {
    if (dcRef.current && dcRef.current.readyState === 'open') {
      const msg = JSON.stringify(payload);
      dcRef.current.send(msg);
      log(`Sent: ${msg}`);
    } else {
      log('Data channel not ready');
    }
  };

  const addNotification = useCallback((message) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 2000);
  }, []);

  // ============ CITY SEARCH HANDLERS ============
  const handleCitySearchChange = (e) => {
    const value = e.target.value;
    setCitySearch(value);

    // Clear previous debounce timer
    if (cityDebounceRef.current) {
      clearTimeout(cityDebounceRef.current);
    }

    // Abort any in-flight request
    if (cityAbortRef.current) {
      cityAbortRef.current.abort();
    }

    if (value.trim().length < 2) {
      setFilteredCities([]);
      setShowCitySuggestions(false);
      setCityLoading(false);
      return;
    }

    setCityLoading(true);

    // Debounce: wait 300ms after user stops typing
    cityDebounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      cityAbortRef.current = controller;

      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(value)}&limit=8&osm_tag=place:city&osm_tag=place:town`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        const cities = (data.features || []).map(f => {
          const p = f.properties || {};
          const parts = [p.name, p.state, p.country].filter(Boolean);
          return parts.join(', ');
        }).filter(Boolean);

        // Deduplicate
        const unique = [...new Set(cities)];

        setFilteredCities(unique);
        setShowCitySuggestions(unique.length > 0);
      } catch (err) {
        if (err.name !== 'AbortError') {
          log(`City search error: ${err.message}`);
          setFilteredCities([]);
          setShowCitySuggestions(false);
        }
      } finally {
        setCityLoading(false);
      }
    }, 300);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setCitySearch(city);
    setShowCitySuggestions(false);
    setFilteredCities([]);
    log(`📍 City selected: ${city}`);
  };

  // Close city suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cityInputRef.current && !cityInputRef.current.contains(e.target)) {
        setShowCitySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { sessionModeRef.current = sessionMode; }, [sessionMode]);

  // Toggle locale in supported locales (multi-select)
  const toggleSupportedLocale = (locale) => {
    setSupportedLocales(prev => {
      if (prev.includes(locale)) {
        // Don't allow removing the last locale
        if (prev.length === 1) return prev;
        return prev.filter(l => l !== locale);
      } else {
        return [...prev, locale];
      }
    });
  };

const handleSessionStart = () => {
  if (dcRef.current && dcRef.current.readyState === 'open') {
    const messageToSend = JSON.stringify({
      message: "session_start",
      persona: personaInput.trim() || "You are a space explorer coach",
      mode: sessionMode,
    });
    log("Sending session start: " + messageToSend);
    dcRef.current.send(messageToSend);
  } else {
    log("Data channel not ready");
  }
};

  const handleSessionEnd = () => {
    if (dcRef.current && dcRef.current.readyState === 'open') {
      log("session_end");
      dcRef.current.send("session_end" + Date.now());
    } else {
      log("Data channel not ready");
    }
  };

  // Send chat message via data channel
  const handleSendChatMessage = () => {
    if (!chatInput.trim() || !dcRef.current || dcRef.current.readyState !== 'open') return;

    const messageToSend = JSON.stringify({
      textonly: 1,
      persona: personaInputRef.current.trim(),
      text: chatInput.trim(),
    });
    dcRef.current.send(messageToSend);
    log("Sent chat: " + messageToSend);

    // Add user message to chat
    setChatMessages(prev => [...prev, { role: 'user', text: chatInput.trim() }]);
    setChatInput('');
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChatMessage();
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Clear any existing timeout - defined first to avoid hoisting issues
  const clearSpeechTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * AUDIO INTERRUPTION METHODS
   */

  // Method 1: Interrupt audio playback when user starts speaking
  const interruptAudioPlayback = useCallback(() => {
    if (!isAudioPlaying || !audioRef.current) return;

    try {
      log("🔇 Interrupting audio playback - user started speaking");
      
      setPausedAudioTime(audioRef.current.currentTime);
      setAudioInterrupted(true);
      
      audioRef.current.pause();
      
      if (audioRef.current.srcObject) {
        const stream = audioRef.current.srcObject;
        const tracks = stream.getAudioTracks();
        tracks.forEach(track => {
          track.enabled = false;
        });
      }

      if (dcRef.current && dcRef.current.readyState === 'open') {
        dcRef.current.send(JSON.stringify({
          type: 'interrupt_audio',
          timestamp: Date.now()
        }));
      }

      setIsAudioPlaying(false);
      log("✅ Audio successfully interrupted and buffered");

    } catch (error) {
      log(`❌ Error interrupting audio: ${error.message}`);
    }
  }, [isAudioPlaying]);

  // Method 2: Resume audio playback after interruption timeout
  const resumeAudioPlayback = useCallback(async () => {
    if (!audioInterrupted || !audioRef.current) return;

    try {
      log("🔊 Attempting to resume audio playback after interruption");

      if (audioRef.current.srcObject) {
        const stream = audioRef.current.srcObject;
        const tracks = stream.getAudioTracks();
        tracks.forEach(track => {
          track.enabled = true;
        });
      }

      if (audioBufferAvailable && audioRef.current.duration > pausedAudioTime) {
        audioRef.current.currentTime = pausedAudioTime;
        
        try {
          await audioRef.current.play();
          log(`✅ Audio resumed from ${pausedAudioTime.toFixed(2)}s`);
          setIsAudioPlaying(true);
        } catch (playError) {
          log(`⚠️ Could not resume audio playback: ${playError.message}`);
        }
      } else {
        log("📭 No audio buffer available for resume - waiting for new content");
      }

      setAudioInterrupted(false);
      setPausedAudioTime(0);

    } catch (error) {
      log(`❌ Error resuming audio: ${error.message}`);
      setAudioInterrupted(false);
    }
  }, [audioInterrupted, pausedAudioTime, audioBufferAvailable]);

  // Method 3: Clear audio buffer and reset state
  const clearAudioBuffer = useCallback(() => {
    try {
      log("🗑️ Clearing audio buffer and resetting playback state");
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        
        if (audioRef.current.srcObject) {
          const stream = audioRef.current.srcObject;
          const tracks = stream.getAudioTracks();
          tracks.forEach(track => track.stop());
        }
      }

      setIsAudioPlaying(false);
      setAudioInterrupted(false);
      setPausedAudioTime(0);
      setAudioBufferAvailable(false);
      
      log("✅ Audio buffer cleared successfully");

    } catch (error) {
      log(`❌ Error clearing audio buffer: ${error.message}`);
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!audioRef.current?.srcObject) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') return;
    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(audioRef.current.srcObject, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        if (audioChunksRef.current.length === 0) return;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const index = ++audioClipIndexRef.current;
        setChatMessages(prev => [...prev, {
          role: 'bot',
          type: 'audio',
          url,
          blob,
          mimeType,
          label: `Bot Audio #${index}`,
          timestamp: new Date().toLocaleTimeString(),
        }]);
        audioChunksRef.current = [];
      };
      recorder.start(250);
      mediaRecorderRef.current = recorder;
    } catch (err) {
      log(`⚠️ Could not start recording: ${err.message}`);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  }, []);

  const setupAudioDetection = useCallback((stream) => {
    // Tear down any previous detection loop
    if (audioDetectionIntervalRef.current) {
      clearInterval(audioDetectionIntervalRef.current);
      audioDetectionIntervalRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    isRecordingActiveRef.current = false;

    let ctx;
    try {
      ctx = new AudioContext();
      audioContextRef.current = ctx;
      ctx.resume().catch(() => {});
    } catch (e) {
      log(`⚠️ AudioContext unavailable: ${e.message}`);
      return;
    }

    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    audioDetectionIntervalRef.current = setInterval(() => {
      // Recording is only active in only_tts mode
      if (sessionModeRef.current !== 'only_tts') {
        if (isRecordingActiveRef.current) {
          isRecordingActiveRef.current = false;
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
          stopRecording();
        }
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      let maxLevel = 0;
      for (let i = 0; i < bufferLength; i++) {
        if (dataArray[i] > maxLevel) maxLevel = dataArray[i];
      }
      const hasAudio = maxLevel > 5;

      if (hasAudio) {
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        if (!isRecordingActiveRef.current) {
          isRecordingActiveRef.current = true;
          startRecording();
        }
      } else {
        if (isRecordingActiveRef.current && !silenceTimerRef.current) {
          silenceTimerRef.current = setTimeout(() => {
            isRecordingActiveRef.current = false;
            silenceTimerRef.current = null;
            stopRecording();
          }, 400);
        }
      }
    }, 50);
  }, [startRecording, stopRecording]);

  const clearChatHistory = useCallback(() => {
    setChatMessages(prev => {
      prev.forEach(msg => {
        if (msg.type === 'audio' && msg.url) URL.revokeObjectURL(msg.url);
      });
      return [];
    });
    audioClipIndexRef.current = 0;
  }, []);

  // Method 4: Enhanced speech detection with audio interruption
  const handleSpeechStart = useCallback(() => {
    if (micMuted) return; // Add this line
    log("🎤 Speech detected - starting/continuing stream");
    setVadStatus('speaking');
    clearSpeechTimeout();

    if (!streamingRef.current) {
      startAudioStreaming();
    }
  }, [micMuted,isAudioPlaying, interruptAudioPlayback, clearSpeechTimeout]);

  // Method 5: Enhanced speech end handling with resume logic
  const handleSpeechEnd = useCallback(() => {
    if (micMuted) return; // Add this line
    log("🔇 Speech ended - starting timeout");
    setVadStatus('timeout');
    
    clearSpeechTimeout();
    
    timeoutRef.current = setTimeout(() => {
      log("⏰ Speech timeout reached - stopping stream");
      stopAudioStreaming();
      setVadStatus('idle');
      
      timeoutRef.current = null;
    }, SPEECH_TIMEOUT);
  }, [micMuted,audioInterrupted, resumeAudioPlayback, clearSpeechTimeout]);

  // Start audio streaming.
  // The mic track is already acquired and attached to the send transceiver at
  // connect time (see acquireMicTrack), so "starting" is just un-muting it.
  // This avoids the getUserMedia + replaceTrack latency that was clipping the
  // first words of each utterance.
  const startAudioStreaming = useCallback(() => {
    if (streamingRef.current) return;
    if (micMuted) return;

    if (!micTrackRef.current) {
      log("Cannot start streaming: mic track not ready yet");
      return;
    }

    micTrackRef.current.enabled = true; // un-mute the already-attached track
    streamingRef.current = true;
    setIsStreaming(true);
    log("Audio streaming started (mic un-muted)");
  }, [micMuted]);

  // Stop audio streaming — mute the track but keep it attached so the next
  // utterance can resume instantly without re-negotiating media.
  const stopAudioStreaming = useCallback(() => {
    if (!streamingRef.current) return;

    if (micTrackRef.current) {
      micTrackRef.current.enabled = false;
    }
    streamingRef.current = false;
    setIsStreaming(false);
    log("Audio streaming stopped (mic muted)");
  }, []);

  // Acquire the mic once and bind it to the sendonly transceiver, left muted.
  // Called during connection setup so audio is flowing the instant VAD fires.
  const acquireMicTrack = useCallback(async () => {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const [micTrack] = micStream.getAudioTracks();
      micTrack.enabled = false; // stay silent until VAD detects speech

      micStreamRef.current = micStream;
      micTrackRef.current = micTrack;

      const sender = sendTransceiverRef.current
        ? sendTransceiverRef.current.sender
        : pcRef.current?.getTransceivers().find(t => t.direction === 'sendonly')?.sender;

      if (sender) {
        await sender.replaceTrack(micTrack);
        log("Mic acquired and attached to send transceiver (muted)");
      } else if (pcRef.current) {
        pcRef.current.addTrack(micTrack);
        log("Mic acquired and added as new track (no sendonly transceiver found)");
      }
    } catch (error) {
      log(`Error acquiring microphone: ${error.message}`);
    }
  }, []);

  const vad = useMicVAD({
    onSpeechStart: handleSpeechStart,
    onSpeechEnd: handleSpeechEnd,
    onVADMisfire: () => {
      log("VAD misfire detected");
    },
    positiveSpeechThreshold: 0.5,  // trigger sooner
    negativeSpeechThreshold: 0.35,
    preSpeechPadFrames: 10,
    minSpeechFrames: 3,            // confirm in ~96ms instead of ~192ms
    redemptionFrames: 12,          // keep — lowering clips the tail
    frameSamples: 512,
    mode: 'NORMAL'
  });



  const toggleMic = useCallback(() => {
    if (!connected) return;

    if (micMuted) {
      // Unmute - hand control back to VAD. Leave the track muted; the next
      // detected speech will un-mute it via startAudioStreaming.
      if (micTrackRef.current) {
        micTrackRef.current.enabled = false;
      }
      setMicMuted(false);
      log("🎤 Mic enabled");
    } else {
      // Mute - disable the mic track and stop streaming
      if (micTrackRef.current) {
        micTrackRef.current.enabled = false;
      }
      if (streamingRef.current) {
        stopAudioStreaming();
      }
      setMicMuted(true);
      setVadStatus('idle');
      clearSpeechTimeout();
      log("🔇 Mic disabled");
    }
  }, [connected, micMuted, stopAudioStreaming, clearSpeechTimeout]);

  const LOCAL_WS_URL = "ws://localhost:8900/ws/";
  const FALLBACK_WS_URL = "wss://miko-external-endpoint.miko4.co.in/ws/"; 

  const resolveWebSocketURL = () =>
    new Promise((resolve) => {
      const probe = new WebSocket(LOCAL_WS_URL);
      const timer = setTimeout(() => {
        probe.close();
        resolve(FALLBACK_WS_URL);
      }, 2000);
      probe.onopen = () => {
        clearTimeout(timer);
        probe.close();
        console.log("Using local WebSocket server:", LOCAL_WS_URL);
        resolve(LOCAL_WS_URL);
      };
      probe.onerror = () => {
        clearTimeout(timer);
        resolve(FALLBACK_WS_URL);
      };
    });
  
  const setupWebRTC = async () => {
    return new Promise((resolve, reject) => {
      const setupTimeout = setTimeout(() => {
        reject(new Error('WebRTC setup timeout'));
      }, 15000);
  
      try {
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            
             {
        urls: [
    "turn:34.134.151.192:3478?transport=udp",
    "turn:34.134.151.192:3478?transport=tcp"
        ],
        username: "automotive_user",
        credential: "automotive_123"
      }
      ],
          bundlePolicy: 'max-bundle'
        });
        pcRef.current = pc;

        let isResolved = false;
        const resolveOnce = () => {
          if (!isResolved) {
            isResolved = true;
            clearTimeout(setupTimeout);
            resolve();
          }
        };

        ws.current.onmessage = async (event) => {
          try {
            const message = JSON.parse(event.data);
            
            switch (message.type) {
              case 'answer':
                console.log('Received answer from server');
                await pc.setRemoteDescription(new RTCSessionDescription(message));
                console.log("Set remote description");
                setConnected(true);
                log("WebRTC connection established");
                resolveOnce();
                break;
                
              case 'ice-candidate':
                if (message.candidate) {
                  console.log('Received ICE candidate from server');
                  await pc.addIceCandidate(new RTCIceCandidate({
                    candidate: message.candidate,
                    sdpMid: message.sdpMid,
                    sdpMLineIndex: message.sdpMLineIndex
                  }));
                }
                break;

              case 'error':
                if (message.message === 'Unauthorized' || message.message === 'client_id_format_error') {
                    clearTimeout(setupTimeout);
                    const errorMsg = message.message === 'client_id_format_error'
                        ? 'Client ID should be a 32-character unique string.'
                        : 'Unauthorized: Invalid API Key or Tenant ID. Please check your credentials.';
                    setAuthError(errorMsg);
                    // Clean up inline instead of calling stopWebRTC (avoids race conditions inside onmessage)
                    if (dcRef.current) {
                        dcRef.current.close();
                        dcRef.current = null;
                    }
                    if (pcRef.current) {
                        pcRef.current.getSenders().forEach(sender => {
                            if (sender.track) sender.track.stop();
                        });
                        pcRef.current.close();
                        pcRef.current = null;
                    }
                    if (micStreamRef.current) {
                        micStreamRef.current.getTracks().forEach(t => t.stop());
                        micStreamRef.current = null;
                    }
                    micTrackRef.current = null;
                    sendTransceiverRef.current = null;
                    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                        ws.current.close(1000, message.message);
                    }
                    ws.current = null;
                    // Reset all state so inputs become editable again
                    streamingRef.current = false;
                    setConnected(false);
                    setIceStatus(false);
                    setIsStreaming(false);
                    setIsAudioPlaying(false);
                    setVadStatus('idle');
                    setSessionActive(false);
                    setIsListening(false);
                    return;
                }
                
             
                  // client_id_format_error


                console.error('WebRTC error from server:', message.error);
                if (!isResolved) {
                  clearTimeout(setupTimeout);
                  reject(new Error(message.error || 'Unknown error'));
                }
                
              default:
                console.log('Unknown message type:', message.type);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            if (!isResolved) {
              clearTimeout(setupTimeout);
              reject(error);
            }
          }
        };

        pc.onicecandidate = async (event) => {
          if (event.candidate && ws.current && ws.current.readyState === WebSocket.OPEN) {
            console.log('Sending ICE candidate via WebSocket');
            try {
              ws.current.send(JSON.stringify({
                type: 'ice-candidate',
                candidate: event.candidate.candidate,
                sdpMid: event.candidate.sdpMid,
                sdpMLineIndex: event.candidate.sdpMLineIndex,
                id: inputClientId || clientId,
                tenant_id: tenantId,
                tenant_api_key: tenantApiKey,
              }));
            } catch (error) {
              console.error(`Error sending ICE candidate: ${error.message}`);
            }
          }
        };

        pc.oniceconnectionstatechange = () => {
          console.log(`ICE connection state: ${pc.iceConnectionState}`);
          
          if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
            console.log('WebRTC connection established');
            setIceStatus(true);
            setTimeout(() => {
              if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                console.log('Closing WebSocket - WebRTC connection established');
                ws.current.close(1000, 'ICE connection established');
                ws.current = null;
                log("WebSocket closed after successful WebRTC connection");
              }
            }, 1000);
          } else if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
            console.log('WebRTC connection lost');
            setIceStatus(false);
            // Defer so we're not calling pc.close() inside an ICE event callback
            setTimeout(() => stopWebRTC('Connection lost — server disconnected.'), 0);
          }
        };

        pc.onconnectionstatechange = () => {
          log(`Connection state: ${pc.connectionState}`);
          
          if (pc.connectionState === 'failed') {
            console.error('WebRTC connection failed');
            setTimeout(() => stopWebRTC('WebRTC connection failed.'), 0);
          }
        };

        sendTransceiverRef.current = pc.addTransceiver("audio", { direction: "sendonly" });
        pc.addTransceiver("audio", { direction: "recvonly" });

        pc.ontrack = (event) => {
          log("Received remote audio track");
          if (audioRef.current) {
            audioRef.current.srcObject = event.streams[0];
            setAudioBufferAvailable(true);
            setupAudioDetection(event.streams[0]);
          }
        };

        const dc = pc.createDataChannel("pingpong");
        dcRef.current = dc;
        
        dc.onopen = () => {
          log("Data channel open");
          lastDcMessageRef.current = Date.now();
          dc.send("Ping: " + Date.now());
          log("first data sent");
          dc.send(JSON.stringify({ type: 'get_voice_list', value: '' }));
          log("Requested voice list");

          // In minimal UI, the connect button also starts the session automatically
          // (the Start-Session button is hidden), using the default persona/mode values.
          if (MINIMAL_UI) {
            const sessionStartMsg = JSON.stringify({
              message: "session_start",
              persona: personaInputRef.current.trim() || "You are a space explorer coach",
              mode: sessionModeRef.current,
            });
            dc.send(sessionStartMsg);
            log("Auto session start (minimal UI): " + sessionStartMsg);
          }
        };

        dc.onmessage = (event) => {
          const text = event.data;
          lastDcMessageRef.current = Date.now();
          log("Received: " + text);

          // Live (interim) transcript: show it as a provisional user bubble and
          // keep replacing it as newer interims arrive.
          if (text.startsWith('user_interim:')) {
            const interimText = text.substring('user_interim:'.length);
            setChatMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'user' && last.interim) {
                const updated = [...prev];
                updated[updated.length - 1] = { ...last, text: interimText };
                return updated;
              }
              return [...prev, { role: 'user', text: interimText, interim: true }];
            });
          }

          // Final transcript: replace the in-progress interim bubble (if any) with
          // the finalized text, otherwise append a new user bubble.
          else if (text.startsWith('user_final:')) {
            const userTranscript = text.substring('user_final:'.length);
            setChatMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'user' && last.interim) {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'user', text: userTranscript };
                return updated;
              }
              return [...prev, { role: 'user', text: userTranscript }];
            });
          }

          // Add these checks after the existing llm_text check
          if (text === 'session_start_ack') {
            setSessionActive(true);
          }
          if (text === 'session_end_ack') {
            setSessionActive(false);
          }

          // Plain-text ACK notification (e.g. session_start_ack)
          if (/^[a-zA-Z0-9_]+_ack$/.test(text.trim())) {
            addNotification(text.trim());
          }

          // Check for LLM text response and add to chat
          if (text.startsWith('llm_text:')) {
            const llmResponse = text.substring('llm_text:'.length);
            setChatMessages(prev => [...prev, { role: 'bot', text: llmResponse }]);
          }

          // Add message to UI log
          setMessageLogs(prev => [...prev, text]);

          try {
            const message = JSON.parse(text);

            if (message.type === 'set_skill_ack') {
              const activeSkill = message.value !== undefined ? message.value : '';
              log(`✅ Skill set: ${activeSkill}`);
              setChatMessages(prev => [
                ...prev,
                { role: 'system', text: `Skill set: ${activeSkill}` },
              ]);
            }
            else if (message.type === 'skill_end') {
              const endedSkill = message.value !== undefined ? message.value : '';
              log(`🎯 Skill ended: ${endedSkill}`);
              setChatMessages(prev => [
                ...prev,
                { role: 'system', text: `Skill ended: ${endedSkill}` },
              ]);
            }
            else if (message.type === 'audio_interrupted') {
              log("🔇 Server acknowledged audio interruption");
            }
            else if (message.type === 'audio_resumed') {
              log("🔊 Server acknowledged audio resume");
            }
            else if (message.type === 'voice_list' && Array.isArray(message.value)) {
              setVoiceList(message.value);
              if (message.value.length > 0) {
                setSelectedVoice(prev => prev || message.value[0]);
              }
              log(`Voice list received: ${message.value.join(', ')}`);
            }
            else if (typeof message.type === 'string' && message.type.endsWith('_ack')) {
              const valueStr = message.value !== undefined && message.value !== ''
                ? ` → ${message.value}`
                : '';
              addNotification(`${message.type}${valueStr}`);
            }
          } catch (e) {
            // Regular ping-pong message
            setTimeout(() => {
              if (dc.readyState === 'open') {
                dc.send("Pong: " + Date.now());
              }
            }, 1000);
          }
        };

        dc.onclose = () => log("Data channel closed");

        const createAndSendOffer = async () => {
          try {
            // Acquire and attach the mic up-front (muted) so the first words
            // aren't lost to getUserMedia/replaceTrack latency at speech start.
            await acquireMicTrack();

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            log("Created offer");
            
            const nameToSend = firstname && firstname.trim() !== "" ? firstname : "buddy";
            const maxSession = maxSessionCount && maxSessionCount !== NaN ? maxSessionCount : 15;
            
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              const offerPayload = {
                id: inputClientId || clientId,
                firstname: nameToSend,
                sdp: offer.sdp,
                type: offer.type,
                tenant_id: tenantId,
                tenant_api_key: tenantApiKey,
                // Session configuration settings
                // supported_locales: supportedLocales,
                // response_mode: responseMode,
                // city: selectedCity || null,
              };
              ws.current.send(JSON.stringify(offerPayload));
              log("Sent offer via WebSocket with settings: " + JSON.stringify({

                supported_locales: supportedLocales,
                response_mode: responseMode,
                city: selectedCity || null,
              }));
              console.log("Sent offer via WebSocket", offerPayload);
            } else {
              throw new Error('WebSocket not ready for sending offer');
            }
            
          } catch (error) {
            clearTimeout(setupTimeout);
            reject(error);
          }
        };

        createAndSendOffer();

      } catch (error) {
        clearTimeout(setupTimeout);
        log(`Error in WebRTC setup: ${error.message}`);
        reject(error);
      }
    });
  };

  const startWebRTC = async () => {
    setAuthError(''); // Add this line
    setSessionActive(false);  // <-- ADD THIS LINE
    return new Promise(async (resolve, reject) => {
      const connectionTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000);

      const wsUrl = await resolveWebSocketURL();
      ws.current = new WebSocket(`${wsUrl}${inputClientId || clientId}`);
      
      ws.current.onopen = async () => {
        clearTimeout(connectionTimeout);
        console.log("WebSocket connection established.");
        
        try {
          await setupWebRTC();
          resolve();
        } catch (error) {
          reject(error);
        }
      };
  
      ws.current.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error("WebSocket connection failed:", error);
        reject(error);
      };
  
      ws.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connection closed:', event.code, event.reason);
        if (!event.wasClean) {
          setTimeout(() => stopWebRTC('WebSocket connection closed unexpectedly.'), 0);
        }
      };
    });
  };
  
  const stopWebRTC = (reason = null) => {
    log("Stopping WebRTC connection");
    setMicMuted(false);
    setSessionActive(false);

    // Clear inactivity watchdog
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }
    lastDcMessageRef.current = null;

    // Stop audio byte detection loop
    if (audioDetectionIntervalRef.current) {
      clearInterval(audioDetectionIntervalRef.current);
      audioDetectionIntervalRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    isRecordingActiveRef.current = false;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    stopRecording();
    clearSpeechTimeout();
    clearAudioBuffer();
    
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }
    
    if (pcRef.current) {
      pcRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.stop();
        }
      });
      pcRef.current.close();
      pcRef.current = null;
    }

    // Release the mic stream/track held since connect time
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    micTrackRef.current = null;
    sendTransceiverRef.current = null;

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.close(1000, 'WebRTC stopped');
    }
    ws.current = null;
    
    streamingRef.current = false;
    setConnected(false);
    setIsStreaming(false);
    setIsAudioPlaying(false);
    setVadStatus('idle');
    setVoiceList([]);
    setSelectedVoice('');
    log("WebRTC connection stopped");
    if (reason) {
      setDisconnectPopup({ show: true, reason });
    }
  };

  useEffect(() => {
    return () => {
      clearSpeechTimeout();
      stopWebRTC();
    };
  }, [clearSpeechTimeout]);

  const [isListening, setIsListening] = useState(false);

  const toggleConnection = async () => {
    if (connected) {
      stopWebRTC('Disconnected by user.');
      setIsListening(false);
    } else {
      await startWebRTC();
    }
  };

  useEffect(() => {
    if (connected) {
      setIsListening(true);
    } else {
      setIsListening(false);
    }
  }, [connected]);

  // Inactivity watchdog — disconnect if no DC message for 10 s
  useEffect(() => {
    if (!connected) return;
    inactivityIntervalRef.current = setInterval(() => {
      if (lastDcMessageRef.current !== null && Date.now() - lastDcMessageRef.current > 10000) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
        lastDcMessageRef.current = null;
        stopWebRTC('No response from server for 10 seconds.');
      }
    }, 1000);
    return () => {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // ============ RESPONSIVE LAYOUT ============
  // Track viewport width so the layout can adapt to mobile / tablet / desktop.
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewportWidth < 768;                       // phones
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024; // tablets
  const isStacked = isMobile;                                 // stack panels vertically

  const getStatusInfo = () => {
    if (!connected) {
      return { text: 'Make sure you fill the connection credentials and click to start WebRTC connection', badge: 'Disconnected', status: 'idle' };
    }
    
    if (vadStatus === 'speaking' && ice_status) {
      return { 
        text: audioInterrupted ? 'Speaking detected (Audio Interrupted)...' : 'Speaking detected...', 
        badge: 'Speaking', 
        status: 'speaking' 
      };
    }
    
    if (vadStatus === 'timeout' && ice_status) {
      return { text: 'Processing speech...', badge: 'Processing', status: 'timeout' };
    }
    
    if (ice_status) {
      const interruptStatus = audioInterrupted ? ' (Audio Paused)' : '';
      return { 
        text: `Connected & Listening - Ready to talk !${interruptStatus} - Click to disconnect`, 
        badge: 'Active', 
        status: 'connected' 
      };
    }
    return { text: 'Connected & Not Ready - Click to disconnect', badge: 'Active', status: 'connected' };
  };

  const statusInfo = getStatusInfo();

  // Common style helpers for settings panel
  const settingsLabelStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
    display: 'block',
  };

  const settingsSelectStyle = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    background: '#ffffff',
    color: '#374151',
    outline: 'none',
    cursor: 'pointer',
  };

  const settingsSectionStyle = {
    marginBottom: '18px',
    paddingBottom: '18px',
    borderBottom: '1px solid #e5e7eb',
  };

  return (
  <div
    className="App"
    style={{
      display: 'flex',
      flexDirection: isStacked ? 'column' : 'row',
      alignItems: 'stretch',   // all panels stretch to full cross-axis size
      height: '100vh',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',      // panels manage their own scrolling
    }}
  >
    {/* ============ LEFT - SETTINGS PANEL ============ */}
    {!MINIMAL_UI && (
    <aside
      style={{
        flex: '0 0 300px',     // fixed width, no grow, no shrink
        width: '300px',
        borderRight: '1px solid #e5e7eb',
        background: '#fafbfc',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Settings Header */}
      <div
        style={{
          padding: '20px 20px 16px 20px',
          borderBottom: '1px solid #e5e7eb',
          background: '#ffffff',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⚙️ Settings
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
          Configure session parameters
        </div>
      </div>
 
      <div style={{ padding: '20px' }}>
        
 
        {/* Supported Locales */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Supported Locales</label>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
            Select one or more
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(LOCALES_BY_STT_PROVIDER[sttProvider] || []).map(opt => (
              <label
                key={opt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: supportedLocales.includes(opt.value) ? '#3b82f6' : '#d1d5db',
                  background: supportedLocales.includes(opt.value) ? '#eff6ff' : '#ffffff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#374151',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="checkbox"
                  checked={supportedLocales.includes(opt.value)}
                  onChange={() => toggleSupportedLocale(opt.value)}
                  style={{ cursor: 'pointer' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <button
            onClick={() => sendDataChannelMessage({ type: 'set_supported_locales', value: supportedLocales })}
            disabled={!connected}
            style={{
              marginTop: '8px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: connected ? '#3b82f6' : '#d1d5db',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: connected ? 'pointer' : 'not-allowed',
            }}
          >
            Set Locales
          </button>
        </div>
 
        {/* Response Mode */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Response Mode</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={responseMode}
              onChange={(e) => setResponseMode(e.target.value)}
              style={{ ...settingsSelectStyle, flex: 1 }}
            >
              {RESPONSE_MODES.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} {opt.value === 'short' ? '' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_response_type', value: responseMode })}
              disabled={!connected}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: connected ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: connected ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>
 
        {/* Age */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Age</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="number"
              value={age}
              onChange={e => setAge(e.target.value)}
              placeholder="e.g. 25"
              min="0"
              style={{
                ...settingsSelectStyle,
                flex: 1,
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_age', value: age })}
              disabled={!connected || age === ''}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && age !== '') ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && age !== '') ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>

        {/* Turn Count */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Turn Count</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="number"
              value={turnCount}
              onChange={e => setTurnCount(e.target.value)}
              placeholder="e.g. 10"
              min="0"
              style={{
                ...settingsSelectStyle,
                flex: 1,
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_turn_count', value: turnCount })}
              disabled={!connected || turnCount === ''}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && turnCount !== '') ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && turnCount !== '') ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>

        {/* Name */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Name</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={settingName}
              onChange={e => setSettingName(e.target.value)}
              placeholder="e.g. Alice"
              style={{
                ...settingsSelectStyle,
                flex: 1,
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_name', value: settingName })}
              disabled={!connected || settingName === ''}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && settingName !== '') ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && settingName !== '') ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>

        {/* Memory Window Size */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Memory Window Size</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="number"
              value={memoryWindowSize}
              onChange={e => setMemoryWindowSize(e.target.value)}
              placeholder="e.g. 10"
              min="0"
              style={{
                ...settingsSelectStyle,
                flex: 1,
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_memory_window_size', value: memoryWindowSize })}
              disabled={!connected || memoryWindowSize === ''}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && memoryWindowSize !== '') ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && memoryWindowSize !== '') ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>

        {/* Skill */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Skill</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={skill}
              onChange={e => setSkill(e.target.value)}
              placeholder="e.g. kiddopia"
              style={{
                ...settingsSelectStyle,
                flex: 1,
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_skill', value: skill })}
              disabled={!connected || skill === ''}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && skill !== '') ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && skill !== '') ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            also sent automatically on session start
          </div>
        </div>

        {/* Voice */}
        <div style={settingsSectionStyle}>
          <label style={settingsLabelStyle}>Voice</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedVoice}
              onChange={e => setSelectedVoice(e.target.value)}
              disabled={voiceList.length === 0}
              style={{
                ...settingsSelectStyle,
                flex: 1,
                background: voiceList.length > 0 ? '#ffffff' : '#f3f4f6',
                cursor: voiceList.length > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              {voiceList.length === 0 ? (
                <option value="">— connect to load voices —</option>
              ) : (
                voiceList.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))
              )}
            </select>
            <button
              onClick={() => sendDataChannelMessage({ type: 'set_voice', value: selectedVoice })}
              disabled={!connected || !selectedVoice}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                background: (connected && selectedVoice) ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: (connected && selectedVoice) ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Set
            </button>
          </div>
        </div>

        {/* City */}
        <div style={{ ...settingsSectionStyle, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
          <label style={settingsLabelStyle}>City</label>
          <div ref={cityInputRef} style={{ position: 'relative' }}>
            <input
              type="text"
              value={citySearch}
              onChange={handleCitySearchChange}
              onFocus={() => {
                if (citySearch.trim().length > 0 && filteredCities.length > 0) {
                  setShowCitySuggestions(true);
                }
              }}
              placeholder="Type to search city..."
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                background: '#ffffff',
                color: '#374151',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {showCitySuggestions && filteredCities.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 20,
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {
                cityLoading ? (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                      Searching...
                    </div>
                  ):
                
                filteredCities.map((city, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleCitySelect(city)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      color: '#374151',
                      cursor: 'pointer',
                      borderBottom: idx < filteredCities.length - 1 ? '1px solid #f3f4f6' : 'none',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}
          </div>
 
          {selectedCity && (
            <div
              style={{
                marginTop: '8px',
                padding: '6px 10px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>📍 {selectedCity}</span>
              <button
                onClick={() => {
                  setSelectedCity('');
                  setCitySearch('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1e40af',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: 0,
                }}
              >
                ✕
              </button>
            </div>
          )}
          <button
            onClick={() => sendDataChannelMessage({ type: 'set_city', value: selectedCity })}
            disabled={!connected}
            style={{
              marginTop: '8px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: connected ? '#3b82f6' : '#d1d5db',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: connected ? 'pointer' : 'not-allowed',
            }}
          >
            Set City
          </button>
        </div>
      </div>
    </aside>
    )}

    {/* ============ MIDDLE - MAIN CONTENT ============ */}
    <main
      style={{
        flex: isStacked ? '0 0 auto' : '1 1 auto',  // fixed-height block when stacked, fill otherwise
        minWidth: 0,                                 // critical so it can shrink, not overflow
        width: isStacked ? '100%' : 'auto',
        height: isStacked ? 'auto' : '100%',
        maxHeight: isStacked ? '55vh' : '100%',      // cap so the chat stays visible on mobile
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        borderBottom: isStacked ? '1px solid #e5e7eb' : 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: isMobile ? '16px 18px' : '22px 28px',
          borderBottom: '1px solid rgba(226,232,240,0.7)',
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <h1 style={{
          margin: 0,
          fontSize: isMobile ? '22px' : '26px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Miko Voice Conversation System
        </h1>
        <p style={{ margin: '6px 0 0 0', color: '#94a3b8', fontSize: '14px', letterSpacing: '0.01em' }}>
          Real-time voice communication
        </p>
      </div>
 
      {/* Content body */}
      <div
        style={{
          flex: 1,
          padding: isMobile ? '16px' : '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isMobile ? '14px' : '20px',
        }}
      >
        {/* Connection Credentials */}
        <div className="soft-card" style={{
          padding: '24px',
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid rgba(226,232,240,0.9)',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.03)',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          width: '100%',
          maxWidth: '500px',
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '17px' }}></span> Connection Credentials
          </div>
 
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>Tenant API Key</label>
            <input
              className="cred-input"
              type="text"
              value={tenantApiKey}
              onChange={(e) => { setTenantApiKey(e.target.value); setAuthError(''); }}
              placeholder="64 character alphanumeric key"
              maxLength={64}
              disabled={connected}
              style={{
                padding: '11px 13px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                fontFamily: 'monospace',
                background: connected ? '#f1f5f9' : '#f8fafc',
                color: connected ? '#9ca3af' : '#0f172a',
                outline: 'none',
              }}
            />
          </div>
 
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>Tenant ID</label>
            <input
              className="cred-input"
              type="text"
              value={tenantId}
              onChange={(e) => { setTenantId(e.target.value); setAuthError(''); }}
              placeholder="32 character ID"
              maxLength={32}
              disabled={connected}
              style={{
                padding: '11px 13px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                fontFamily: 'monospace',
                background: connected ? '#f1f5f9' : '#f8fafc',
                color: connected ? '#9ca3af' : '#0f172a',
                outline: 'none',
              }}
            />
          </div>
 
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>Client ID</label>
            <input
              className="cred-input"
              type="text"
              value={inputClientId}
              onChange={(e) => { setInputClientId(e.target.value); setAuthError(''); }}
              placeholder="32 character alphanumeric ID"
              maxLength={32}
              disabled={connected}
              style={{
                padding: '11px 13px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                fontFamily: 'monospace',
                background: connected ? '#f1f5f9' : '#f8fafc',
                color: connected ? '#9ca3af' : '#0f172a',
                outline: 'none',
              }}
            />
          </div>
 
          {authError && (
            <div style={{
              padding: '10px 14px',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '13px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              {authError}
            </div>
          )}
        </div>
 
        <StatusBadge status={statusInfo.status}>
          <StatusDot active={vadStatus === 'speaking' || isListening} />
          {statusInfo.badge}
        </StatusBadge>
 
        <ButtonWrapper>
          <VoiceButton
            onClick={toggleConnection}
            connected={connected}
            listening={connected && isListening}
            disabled={!connected && (!tenantApiKey || !tenantId || !inputClientId)}
          >
            {connected ? (
              <LottieWrapper>
                <Lottie animationData={listeningAnimation} loop autoplay />
              </LottieWrapper>
            ) : (
              <ConnectionIcon connected={connected}>🔌</ConnectionIcon>
            )}
          </VoiceButton>
        </ButtonWrapper>
 
        <StatusText>{statusInfo.text}</StatusText>
 
        {connected && (
          <button
            onClick={toggleMic}
            style={{
              padding: '10px 20px',
              backgroundColor: micMuted ? '#ef4444' : '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.2s',
            }}
          >
            <span style={{ fontSize: '18px' }}>{micMuted ? '🔇' : '🎤'}</span>
            {micMuted ? 'Unmute Mic' : 'Mute Mic'}
          </button>
        )}
 
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
          Please reload the page if you cannot connect!
        </div>

        {!MINIMAL_UI && (
        <>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          padding: '14px 16px',
          borderRadius: '12px',
          background: connected ? 'linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)' : '#f3f4f6',
          border: connected ? '1px solid #c4b5fd' : '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '20px' }}>🎭</span>
            <span style={{
              fontSize: '16px',
              fontWeight: 700,
              color: connected ? '#6d28d9' : '#9ca3af',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              AI Persona
            </span>
          </div>
          <input
            type="text"
            value={personaInput}
            onChange={e => {
              const val = e.target.value;
              const words = val.trim().split(/\s+/).filter(Boolean);
              if (words.length <= 10000 || val.trim() === '') {
                personaInputRef.current = val;
                setPersonaInput(val);
              }
            }}
            disabled={!connected}
            placeholder="e.g. you are an astronaut"
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: connected ? '1.5px solid #a78bfa' : '1.5px solid #d1d5db',
              fontSize: '15px',
              color: connected ? '#4c1d95' : '#9ca3af',
              background: connected ? 'rgba(255,255,255,0.85)' : '#ececec',
              cursor: connected ? 'text' : 'not-allowed',
              outline: 'none',
              width: '220px',
              textAlign: 'center',
              boxShadow: connected ? '0 0 0 3px rgba(167,139,250,0.15)' : 'none',
              transition: 'all 0.2s ease',
            }}
          />
          {connected && (
            <>
              <button
                onClick={() => sendDataChannelMessage({ type: 'set_persona', value: personaInput.trim() })}
                style={{
                  padding: '6px 18px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#7c3aed',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Set Persona
              </button>
              <span style={{ fontSize: '11px', color: '#7c3aed', opacity: 0.7 }}>
                 also sent on session start
              </span>
            </>
          )}
        </div>

        {/* Session Response Mode */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          padding: '14px 16px',
          borderRadius: '12px',
          background: connected ? '#f0fdf4' : '#f3f4f6',
          border: connected ? '1px solid #86efac' : '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '20px' }}>🎚️</span>
            <span style={{
              fontSize: '16px',
              fontWeight: 700,
              color: connected ? '#15803d' : '#9ca3af',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              Response Mode
            </span>
          </div>
          <select
            value={sessionMode}
            onChange={(e) => setSessionMode(e.target.value)}
            disabled={!connected}
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: connected ? '1.5px solid #86efac' : '1.5px solid #d1d5db',
              fontSize: '14px',
              color: connected ? '#15803d' : '#9ca3af',
              background: connected ? 'rgba(255,255,255,0.85)' : '#ececec',
              cursor: connected ? 'pointer' : 'not-allowed',
              outline: 'none',
              width: '220px',
              textAlign: 'center',
              boxShadow: connected ? '0 0 0 3px rgba(134,239,172,0.2)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <option value="normal">Normal</option>
            <option value="only_stt">STT</option>
            <option value="only_tts">TTS</option>
          </select>
          {connected && (
            <>
              <button
                onClick={() => sendDataChannelMessage({ type: 'set_mode', value: sessionMode })}
                style={{
                  padding: '6px 18px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#15803d',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Set Mode
              </button>
              <span style={{ fontSize: '11px', color: '#16a34a', opacity: 0.7 }}>
                also sent on session start
              </span>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={handleSessionStart}
            disabled={!connected || sessionActive}
            style={{
              padding: '12px 24px',
              backgroundColor: (connected && !sessionActive) ? '#10b981' : '#d1d5db',
              cursor: (connected && !sessionActive) ? 'pointer' : 'not-allowed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Start New Session
          </button>
 
          <button
            onClick={handleSessionEnd}
            disabled={!connected || !sessionActive}
            style={{
              padding: '12px 24px',
              backgroundColor: (connected && sessionActive) ? '#10b981' : '#d1d5db',
              cursor: (connected && sessionActive) ? 'pointer' : 'not-allowed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Stop current Session
          </button>
        </div>

        {/* Custom Datachannel Message */}
        <div style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '14px 16px',
          borderRadius: '12px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
            Send Custom Message
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={customMsgType}
              onChange={e => setCustomMsgType(e.target.value)}
              placeholder="type"
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                outline: 'none',
                background: '#ffffff',
                color: '#374151',
              }}
            />
            <input
              type="text"
              value={customMsgValue}
              onChange={e => setCustomMsgValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  sendDataChannelMessage({ type: customMsgType, value: customMsgValue });
                }
              }}
              placeholder="value"
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                outline: 'none',
                background: '#ffffff',
                color: '#374151',
              }}
            />
            <button
              onClick={() => sendDataChannelMessage({ type: customMsgType, value: customMsgValue })}
              disabled={!connected || !customMsgType.trim()}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: (connected && customMsgType.trim()) ? '#3b82f6' : '#d1d5db',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 500,
                cursor: (connected && customMsgType.trim()) ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >
              Send
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </main>

    {/* ============ RIGHT - CHAT PANEL ============ */}
    <aside
      style={{
        // Desktop: fixed sidebar. Tablet: slightly narrower. Mobile: full-width, fills remaining height.
        flex: isStacked ? '1 1 auto' : `0 0 ${isTablet ? '320px' : '380px'}`,
        width: isStacked ? '100%' : (isTablet ? '320px' : '380px'),
        minWidth: 0,
        minHeight: 0,          // allow the inner message list to scroll instead of overflowing
        borderLeft: isStacked ? 'none' : '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        height: isStacked ? 'auto' : '100%',
      }}
    >
      {/* Chat Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: '#f9fafb',
        }}
      >
        {/* Left: title */}
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', flex: '0 0 auto' }}>
          💬 Conversation
        </div>

        {/* Centre: connection status — absolutely centred so it never shifts (hidden on mobile to avoid overlap) */}
        {!isMobile && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '12px',
            padding: '4px 10px',
            borderRadius: '12px',
            background: connected ? '#d1fae5' : '#f3f4f6',
            color: connected ? '#065f46' : '#9ca3af',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {connected ? 'Connected' : 'Disconnected'}
        </div>
        )}

        {/* Right: clear button */}
        <button
          className="clear-btn"
          onClick={clearChatHistory}
          title="Clear all chat history and audio clips"
          style={{
            marginLeft: 'auto',
            flex: '0 0 auto',
            padding: '5px 12px',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            background: '#ffffff',
            color: '#6b7280',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          🗑 Clear
        </button>
      </div>
 
      {/* Locale Selector */}
      {!MINIMAL_UI && (
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <label
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#6b7280',
            whiteSpace: 'nowrap',
          }}
        >
          Locale:
        </label>
        <select
          value={selectedLocale}
          onChange={(e) => setSelectedLocale(e.target.value)}
          disabled={!connected}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            background: connected ? '#ffffff' : '#f3f4f6',
            color: connected ? '#374151' : '#9ca3af',
            fontSize: '13px',
            cursor: connected ? 'pointer' : 'not-allowed',
            outline: 'none',
          }}
        >
          {LOCALE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* Chat Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          background: 'linear-gradient(180deg, #fbfcfe 0%, #f4f6fa 100%)',
        }}
      >
        {chatMessages.length === 0 ? (
          <div
            className="empty-pulse"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#94a3b8',
              gap: '12px',
              padding: '0 24px',
            }}
          >
            <div style={{ fontSize: '40px', lineHeight: 1 }}>{connected ? '🎙️' : '💬'}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
              {connected ? 'Listening — start speaking' : 'Connect to start the conversation'}
            </div>
            <div style={{ fontSize: '12px', color: '#a3aec0' }}>
              {connected
                ? 'Your transcript and the assistant’s replies will appear here.'
                : 'Enter your credentials and connect to begin.'}
            </div>
          </div>
        ) : (
          chatMessages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : msg.role === 'system' ? 'center' : 'flex-start',
              }}
            >
              {msg.role === 'system' ? (
                <div
                  className="chat-bubble"
                  style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '999px',
                    padding: '3px 12px',
                    fontStyle: 'italic',
                  }}
                >
                  🎯 {msg.text}
                </div>
              ) : msg.type === 'audio' ? (
                <div
                  className="chat-bubble"
                  style={{
                    maxWidth: '92%',
                    padding: '10px 14px',
                    borderRadius: '16px 16px 16px 4px',
                    background: '#f0fdf4',
                    border: '1px solid #86efac',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: '#15803d', fontWeight: 600 }}>
                      🔊 {msg.label}
                    </span>
                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>{msg.timestamp}</span>
                  </div>
                  <audio
                    src={msg.url}
                    controls
                    style={{ width: '100%', height: '32px', accentColor: '#10b981' }}
                  />
                  <a
                    href={msg.url}
                    download={`${msg.label.replace(/\s+/g, '-').toLowerCase()}.${msg.mimeType === 'audio/webm' ? 'webm' : 'mp4'}`}
                    style={{
                      fontSize: '11px',
                      color: '#15803d',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    ⬇ Download
                  </a>
                </div>
              ) : (
                <div
                  className="chat-bubble"
                  style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius:
                      msg.role === 'user'
                        ? '16px 16px 4px 16px'
                        : '16px 16px 16px 4px',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
                      : '#ffffff',
                    color: msg.role === 'user' ? '#ffffff' : '#1e293b',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    boxShadow: msg.role === 'user'
                      ? '0 2px 8px rgba(79,70,229,0.25)'
                      : '0 1px 3px rgba(15,23,42,0.08)',
                    border: msg.role === 'bot' ? '1px solid #eef2f7' : 'none',
                    wordBreak: 'break-word',
                    opacity: msg.interim ? 0.7 : 1,           // dim while still being transcribed
                    fontStyle: msg.interim ? 'italic' : 'normal',
                  }}
                >
                  {msg.role === 'bot' && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#10b981',
                        fontWeight: 600,
                        marginBottom: '4px',
                      }}
                    >
                      Assistant
                    </div>
                  )}
                  {msg.text}
                  {msg.interim && (
                    <span className="empty-pulse" style={{ marginLeft: '4px' }}>…</span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>
 
      {/* Chat Input */}
      {!MINIMAL_UI && (
      <div
        style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          background: '#ffffff',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <textarea
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={handleChatKeyDown}
          disabled={!connected}
          placeholder={connected ? 'Type a message...' : 'Connect first...'}
          rows={1}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: '12px',
            border: '1px solid #d1d5db',
            background: connected ? '#ffffff' : '#f3f4f6',
            color: connected ? '#374151' : '#9ca3af',
            fontSize: '14px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            minHeight: '42px',
            maxHeight: '100px',
            cursor: connected ? 'text' : 'not-allowed',
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
          }}
        />
        <button
          onClick={handleSendChatMessage}
          disabled={!connected || !chatInput.trim()}
          style={{
            padding: '10px 18px',
            borderRadius: '12px',
            border: 'none',
            background: connected && chatInput.trim() ? '#3b82f6' : '#d1d5db',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: connected && chatInput.trim() ? 'pointer' : 'not-allowed',
            whiteSpace: 'nowrap',
            minHeight: '42px',
            transition: 'background 0.2s',
          }}
        >
          Send
        </button>
      </div>
      )}
    </aside>

    <audio
      ref={audioRef}
      autoPlay
      className="audio-player"
      onPlay={() => { setIsAudioPlaying(true); log("🔊 Audio started playing"); }}
      onPause={() => { setIsAudioPlaying(false); log("⏸️ Audio paused"); }}
      onEnded={() => {
        setIsAudioPlaying(false);
        setAudioBufferAvailable(false);
        setAudioInterrupted(false);
        log("🔚 Audio ended - resetting states");
      }}
      onCanPlay={() => { setAudioBufferAvailable(true); log("📦 Audio buffer ready for playback"); }}
      onError={(e) => {
        log(`❌ Audio error: ${e.target.error?.message || 'Unknown error'}`);
        setIsAudioPlaying(false);
        setAudioBufferAvailable(false);
      }}
    />

    {/* ACK Toast Notifications */}
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 900,
        pointerEvents: 'none',
      }}
    >
      {notifications.map(n => (
        <div
          key={n.id}
          style={{
            padding: '10px 16px',
            background: '#1e293b',
            color: '#f0fdf4',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#4ade80', fontSize: '15px' }}>✓</span>
          {n.message}
        </div>
      ))}
    </div>

    {/* Disconnect Popup */}
    {disconnectPopup.show && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '36px 32px 28px',
            maxWidth: '360px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
          }}
        >
          <div style={{ fontSize: '44px', marginBottom: '14px' }}>🔌</div>
          <div style={{ fontSize: '19px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>
            Disconnected
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '28px', lineHeight: '1.6' }}>
            {disconnectPopup.reason}
          </div>
          <button
            onClick={() => setDisconnectPopup({ show: false, reason: '' })}
            style={{
              padding: '10px 36px',
              borderRadius: '8px',
              border: 'none',
              background: '#3b82f6',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}
  </div>
  );
}

export default App;
